<template>
  <ChartFrame :title="$t('Chart__Sectors__name')">
    <Switcher :options="chartTypes" v-model="currentChartType" />
    <RadioButtons :options="sectors" v-model="currentSector" />
    <RadioButtons :options="years" v-model="currentYear" />
    <ApexChart
      v-show="currentChartType === chartTypes[1]"
      :height="500"
      :options="optionsTreemap"
      :series="currentTreemapSeries"
    />
    <ApexChart
      v-show="currentChartType === chartTypes[0]"
      ref="barChart"
      :height="800"
      :options="optionsBar"
      :series="currentBarSeries"
    />
  </ChartFrame>
</template>

<script>
import ChartFrame from '@/components/ChartPartials/ChartFrame';
import RadioButtons from '@/components/Ui/RadioButtons';
import Switcher from '@/components/Ui/Switcher';
import dataTreemap from '@/assets/json/SectorsTreemap.json';
import dataBar from '@/assets/json/SectorsBar.json';
import { sortAxisBasedOnDataValues } from '@/utils';
import { apexChartsDefaults } from '@/components/ChartPartials/setApexChartsDefaults';

export default {
  name: 'Sectors',
  components: {
    ChartFrame,
    RadioButtons,
    Switcher
  },
  data() {
    return {
      chartTypes: [
        this.$t('ChartType__BarChart__name'),
        this.$t('ChartType__Treemap__name')
      ],
      sheetsTreemap: dataTreemap,
      sheetsBar: dataBar,
      years: Array.from(
        new Set(dataTreemap[0].series.map(s => s.name.split('/')[0]))
      ).sort((a, b) => parseInt(a) - parseInt(b)),
      sectors: Array.from(
        new Set(dataTreemap[0].series.map(s => s.name.split('/')[1]))
      ).sort(a => (a === 'Total' ? 1 : -1)),
      currentYearData: null,
      currentSectorData: null,
      currentChartTypeData: null,
      xAxisOriginal: dataBar[0].axis,
      optionsTreemap: {
        chart: {
          type: 'treemap'
        },
        dataLabels: {
          enabled: true,
          formatter(val, opts) {
            return opts.value > 5 ? val : '';
          }
        },
        legend: {
          show: false
        }
      },
      optionsBar: {
        dataLabels: {
          background: {
            enabled: true,
            foreColor: apexChartsDefaults.colors[0]
          }
        },
        chart: {
          type: 'bar'
        },
        plotOptions: {
          bar: {
            horizontal: true
          }
        },
        xaxis: {
          categories: []
        },
        legend: {
          show: false
        }
      }
    };
  },
  computed: {
    currentChartType: {
      get() {
        return this.currentChartTypeData
          ? this.currentChartTypeData
          : this.chartTypes[0];
      },
      set(val) {
        this.currentChartTypeData = val;
      }
    },
    currentYear: {
      get() {
        return this.currentYearData
          ? this.currentYearData
          : this.years[this.years.length - 1];
      },
      set(val) {
        this.currentYearData = val;
      }
    },
    currentSector: {
      get() {
        return this.currentSectorData
          ? this.currentSectorData
          : this.sectors[0];
      },
      set(val) {
        this.currentSectorData = val;
      }
    },
    currentTreemapSeries() {
      const currentTreemapSeries = this.sheetsTreemap[0].series.find(
        s => s.name === `${this.currentYear}/${this.currentSector}`
      );
      return [
        {
          name: currentTreemapSeries.name,
          data: currentTreemapSeries.data.map(item => ({
            x: this.$t(item.x),
            y: item.y
          }))
        }
      ];
    },
    currentBarSeries() {
      const currentSeries = this.sheetsBar[0].series.find(
        s => s.name === `${this.currentYear}/${this.currentSector}`
      );
      const nameParts = currentSeries.name.split('/');
      const name = `${nameParts[0]}/${this.$t(nameParts[1])}`;
      return [
        {
          name,
          data: currentSeries.data.slice(0).sort((a, b) => b - a)
        }
      ];
    }
  },
  watch: {
    currentBarSeries: {
      immediate: true,
      deep: true,
      /**
       * Re-ordering xAxis in a descending order, based on the
       * data values in the currentBarSeries
       * @param currentBarSeries
       */
      handler(currentBarSeries) {
        const currentSeries = this.sheetsBar[0].series.find(
          s => s.name === `${this.currentYear}/${this.currentSector}`
        );
        let newXAxis = sortAxisBasedOnDataValues(
          currentSeries,
          this.xAxisOriginal,
          currentBarSeries
        );
        // translate categories
        newXAxis = newXAxis.map(cat => this.$t(cat));
        this.$set(this.optionsBar.xaxis, 'categories', newXAxis);
        if (this.$refs.barChart && this.$refs.barChart.chart) {
          this.$refs.barChart.chart.updateOptions(this.optionsBar);
        }
      }
    }
  }
};
</script>

<style scoped></style>
